/* eslint-disable react/display-name */
import { Button, Input, message, Modal, Space, Table, TableProps, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styles from 'src/styles/layout.module.css';
import { useDevicesByCompanyId } from 'src/features/devices/devices.hooks';
import DevicesTable from 'src/components/pages/Company/CompanyPage/CompanyDevicesTab/DevicesTable';
import { Device } from 'src/features/devices/devices.types';
import { fetchPhyDevices } from '../../../../../features/phy_devices/state';
import { fetchDeviceTypes } from '../../../../../features/devicesTypes/devicesTypes.state';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/hooks';
import { selectDevices } from '../../../../../features/phy_devices/selector';
import { PhyDevice } from '../../../../../features/phy_devices/types';
import tileStyles from '../../../../../styles/tile.module.css';
import { SearchOutlined } from '@ant-design/icons';
import { useDebounce } from 'react-use';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfirmButton from '../../../../atoms/ConfirmButton';
import { createCompanyDevice } from '../../../../../features/devices/devices.state';

const CompanyDevicesTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const [addHeadset, setAddHeadset] = useState(false);
  const [creatingCompanyDevice, setCreatingCompanyDevice] = useState(false);
  const [devices, { fetchCompanyDevices, removeCompanyDevice, addNewDevice }] = useDevicesByCompanyId({
    companyId,
  });
  const allDevices = useAppSelector(selectDevices);
  const dispatch = useAppDispatch();
  const [name, setName] = useState<string | undefined>(undefined);
  const intl = useIntl();

  const newDevices = useMemo(() => {
    const ids = devices.map(d => d.id);
    return allDevices.filter(d => !ids.includes(d.id));
  }, [allDevices, devices]);

  useEffect(() => {
    fetchCompanyDevices();
    dispatch(fetchPhyDevices({}));
  }, [dispatch, fetchCompanyDevices]);

  useDebounce(
    () => {
      if (name !== undefined) {
        dispatch(fetchPhyDevices({ query: name }));
      }
    },
    300,
    [dispatch, name]
  );

  const onCreateCompanyDevice = async (deviceId: string) => {
    setCreatingCompanyDevice(true);
    const res = await dispatch(createCompanyDevice({ companyId, deviceId }));
    if (createCompanyDevice.fulfilled.match(res)) {
      setAddHeadset(false);
    } else {
      message.error(
        intl.formatMessage({
          id: 'error.сreateCompanyDevice',
          defaultMessage: 'Ошибка при добавлении шлема в компанию',
        })
      );
    }
    setCreatingCompanyDevice(false);
  };

  const tableConfig = useMemo<TableProps<PhyDevice>>(() => {
    const result: TableProps<PhyDevice> = {
      dataSource: newDevices,
      rowKey: values => values.id,
      columns: [
        {
          title: 'Шлем',
          dataIndex: 'label',
          key: 'label',
          render: (label: string, device) => (
            <Space direction={'horizontal'} style={{ justifyContent: 'space-between', width: '100%' }}>
              <Space direction={'vertical'} size={0}>
                <Link to={`/settings/devices/${device.id}`} className={tileStyles.tileName}>
                  <span>{device.companyDevice?.label || device.id}</span>
                </Link>
                <Typography.Text type={'secondary'} style={{ fontSize: '12px' }}>
                  <Space>
                    <span>{device.id}</span>|<span>{device.deviceType.name}</span>|<span>{device.firmware || '-'}</span>
                  </Space>
                </Typography.Text>
              </Space>
            </Space>
          ),
        },
        {
          title: 'Идентификатор',
          dataIndex: 'humanId',
          key: 'humanId',
          align: 'right',
          render: value => <Typography.Text style={{ fontSize: '12px' }}>{value}</Typography.Text>,
        },
        {
          title: 'Отель',
          dataIndex: 'companyDevice',
          key: 'companyDevice',
          align: 'right',
          render: (v, r) =>
            r.companyDevice ? (
              <Link to={`/commercial/companies/${r.companyDevice.company.id}`} className={tileStyles.tileName}>
                <span>{r.companyDevice?.company.name}</span>
              </Link>
            ) : (
              '-'
            ),
        },
        {
          title: 'Действия',
          dataIndex: 'companyDevice',
          key: 'action',
          align: 'center',
          render: (_, r) => (
            <ConfirmButton type={'dashed'} onClick={() => onCreateCompanyDevice(r.id)} disabled={creatingCompanyDevice}>
              <FormattedMessage id={'action.add'} />
            </ConfirmButton>
          ),
        },
      ],
    };
    return result;
  }, [devices]);

  return (
    <div className={styles.root}>
      <Modal
        open={addHeadset}
        title={`Добавление шлема`}
        footer={null}
        onCancel={() => setAddHeadset(false)}
        width={1024}
        bodyStyle={{ maxHeight: '800px' }}
      >
        <Space direction={'vertical'} style={{ width: '100%', overflow: 'hidden' }}>
          <Input
            autoFocus
            allowClear
            placeholder={intl.formatMessage({ id: 'placeholder.search' })}
            prefix={<SearchOutlined />}
            onChange={e => {
              setName(e.target.value);
            }}
          />
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <Table {...tableConfig} size='middle' pagination={{ pageSize: 50 }} />
          </div>
        </Space>
      </Modal>
      <div className={styles.header}>
        <Space
          direction={'horizontal'}
          style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, width: '100%' }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            Девайсы
          </Typography.Title>
          <Button type={'primary'} onClick={() => setAddHeadset(true)}>
            Добавить шлем
          </Button>
        </Space>
      </div>
      <div className={styles.content}>
        <DevicesTable devices={devices} onRemove={(h: Device) => removeCompanyDevice({ deviceId: h.id })} />
      </div>
    </div>
  );
};

export default CompanyDevicesTab;
